
import { useState } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useBookingStatuses, useDeleteBookingStatus, useUpdateBookingStatusOrder } from "@/hooks/useEventManagement";
import { BookingStatusItem } from "./BookingStatusItem";
import { supabase } from "@/integrations/supabase/client";

interface BookingStatusesListProps {
  onEdit: (statusId: string) => void;
}

export function BookingStatusesList({ onEdit }: BookingStatusesListProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<string | null>(null);
  const [affectedTransitionsCount, setAffectedTransitionsCount] = useState(0);
  const [reordering, setReordering] = useState(false);

  const { data: statuses, isLoading, error } = useBookingStatuses();
  const deleteStatus = useDeleteBookingStatus();
  const updateStatusOrder = useUpdateBookingStatusOrder();

  const handleDelete = async (statusId: string) => {
    setStatusToDelete(statusId);
    
    // Find the status to get its status_key
    const statusToDeleteData = statuses?.find(s => s.id === statusId);
    if (statusToDeleteData) {
      // Count affected event flows (open transitions architecture)
      const { data: flowsData } = await supabase
        .from('event_flows')
        .select('id')
        .or(`from_status.eq.${statusToDeleteData.status_key},to_status.eq.${statusToDeleteData.status_key}`);
      
      setAffectedTransitionsCount(flowsData?.length || 0);
    }
    
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (statusToDelete) {
      await deleteStatus.mutateAsync(statusToDelete);
      setDeleteDialogOpen(false);
      setStatusToDelete(null);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !statuses) return;

    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update sort_order based on new positions
    const updates = items.map((item, index) => ({
      id: item.id,
      sort_order: index + 1,
    }));

    setReordering(true);
    try {
      await updateStatusOrder.mutateAsync(updates);
    } finally {
      setReordering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        <p>Error loading booking statuses</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : "Unknown error"}
        </p>
      </div>
    );
  }

  if (!statuses || statuses.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No booking statuses found</p>
        <p className="text-sm">Create your first status to get started</p>
      </div>
    );
  }

  return (
    <>
      <div>
        {reordering && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-sm text-blue-800">Updating booking status order...</span>
          </div>
        )}
        
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="booking-statuses">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {statuses.map((status, index) => (
                  <BookingStatusItem
                    key={status.id}
                    status={status}
                    index={index}
                    onEdit={onEdit}
                    onDelete={handleDelete}
                    reordering={reordering}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking Status</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to delete this booking status? This action cannot be undone
                and may affect existing bookings.
              </p>
              {affectedTransitionsCount > 0 && (
                <p className="text-amber-600 font-medium">
                  ⚠️ This will affect {affectedTransitionsCount} event flow{affectedTransitionsCount === 1 ? '' : 's'} 
                  that reference this status.
                </p>
              )}
              {affectedTransitionsCount === 0 && (
                <p className="text-green-600">
                  ✓ No event flows will be affected by this deletion.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
